import * as Sentry from '@sentry/react'

function isMessageBusBreadcrumb(breadcrumb) {
  // We do requests to the message-bus endpoint very frequently
  // and they turn into noise. If anything critical happens
  // related to message bus we will probably get an exception.

  if (breadcrumb.category !== 'fetch' && breadcrumb.category !== 'xhr')
    return false

  return String(breadcrumb.data?.url).includes('/message-bus/')
}

function messagesIn(event) {
  const values = event.exception?.values || []

  return [event.message, ...values.map((value) => value.value)].filter(Boolean)
}

// Avoids unbounded transaction cardinality from locales and auction IDs in the path.
function transactionName() {
  return location.pathname
    .replace(/^\/[a-z]{2}(?=\/|$)/, '/:locale')
    .replace(/\/\d+(?=\/|$)/g, '/:auctionId')
}

function configureSentry({ backendBaseUrl }) {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const release = import.meta.env.VITE_GIT_REVISION
  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT || process.env.NODE_ENV
  const isTest = process.env.NODE_ENV === 'test'

  if (!dsn && !isTest) {
    console.warn('Missing Sentry DSN')
  }

  // Browser tracing instruments web vitals through performance.getEntriesByType and PerformanceObserver, neither of which jsdom implements.
  if (isTest) return Sentry

  const isProduction = environment === 'production'

  let stopSendingEvents = false

  Sentry.init({
    dsn,
    environment,
    release,

    ignoreErrors: [
      // Assumed to be old browsers not supporting RTCIceCandidate or RTCPeerConnection.
      // Not sure why we've seen it on Safari 16.3 which is supposed to support this; maybe a bot lying about its user agent?
      "Can't find variable: RTCIceCandidate",
      "Can't find variable: RTCPeerConnection",

      // Safe to ignore: https://stackoverflow.com/a/64257593/6962
      'ResizeObserver loop completed with undelivered notifications',

      // Safe to ignore: Fixinator security scanner: https://github.com/foundeo/fixinator
      'window.fixinatorInputs.has',

      // Internal Immer cleanup failure when revoking a draft (Immer's private `type_` field).
      // Proxy quirk on old/unusual platforms (Tizen, old mobile Chrome). Not actionable.
      "Cannot read properties of undefined (reading 'type_')",
    ],

    denyUrls: [
      /^moz-extension:\/\//,
      /^chrome-extension:\/\//,
      // Cloudflare's auto-injected RUM beacon. Third-party code we can't fix.
      /static\.cloudflareinsights\.com\/beacon\.min\.js/,
    ],

    beforeSend(event) {
      if (stopSendingEvents) return null

      // Ignore errors from known bots.
      if (navigator.userAgent?.includes('Linespider')) return null

      // Likely from Microsoft Outlook SafeLink Crawler:
      // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062
      if (
        messagesIn(event).some((message) =>
          message.includes('Object Not Found Matching Id:')
        )
      ) {
        // We've seen other, less clearly identified errors follow on from this one (they have
        // this one in their breadcrumbs). This is the least bad way we know to identify them.
        // So we just ignore all future errors.
        stopSendingEvents = true
        return null
      }

      return event
    },

    beforeBreadcrumb(breadcrumb) {
      return isMessageBusBreadcrumb(breadcrumb) ? null : breadcrumb
    },

    integrations: [
      Sentry.browserTracingIntegration({
        beforeStartSpan: (context) => ({ ...context, name: transactionName() }),
        shouldCreateSpanForRequest: (url) => !url.includes('/message-bus/'),
      }),

      // Masks text, inputs and media by default.
      Sentry.replayIntegration(),
    ],

    tracesSampleRate: isProduction ? 0.05 : 1.0,
    // The backend is a separate origin, so it needs listing for the trace to continue into live-bidding-api.
    tracePropagationTargets: [
      /^\//,
      window.location.origin,
      backendBaseUrl.replace(/^\/\//, ''),
    ],

    replaysSessionSampleRate: isProduction ? 0.01 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    sendDefaultPii: true,
    streamGenAiSpans: false,
  })

  return Sentry
}

export default configureSentry
